/* ======================================================================
   CART DRAWER LINES — native replacement for the AMP Slide Cart app
   ----------------------------------------------------------------------
   One <cart-drawer-line> per item in sections/cart-drawer.liquid. It
   changes quantities through /cart/change.js and hands the result to the
   theme the same way Prestige's own controls do: a "cart:change" event
   carrying the re-rendered drawer section. Prestige's <cart-drawer> then
   swaps the drawer content and the header count updates itself.

   What this adds over Prestige's line-item-quantity:
     - the line is marked busy (aria-busy, dimmed) while a request runs
     - network failures and refusals show a message on the line instead
       of leaving the loading bar stuck
     - quick taps on + / − are merged into one request
     - focus returns to the control the shopper was using after the
       drawer re-renders, and the change is announced to screen readers
   ====================================================================== */

(() => {
  if (window.customElements.get('cart-drawer-line')) return;

  const root = () => window.Shopify?.routes?.root || '/';
  let pendingFocusId = null;
  let pendingAnnouncement = '';
  let pendingLineError = null;

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  class CartDrawerLine extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.klv-cart-qty__input');
      this.errorElement = this.querySelector('[data-line-error]');
      this.timer = null;

      this.addEventListener('click', this.onClick);
      this.input?.addEventListener('change', this.onInputChange);
      this.input?.addEventListener('keydown', this.onInputKeydown);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    get key() {
      return this.dataset.lineKey;
    }

    get currentQuantity() {
      return parseInt(this.dataset.quantity, 10) || 0;
    }

    onClick = (event) => {
      const button = event.target.closest('[data-action]');
      if (!button || button.disabled || this.getAttribute('aria-busy') === 'true') return;

      const step = parseInt(button.dataset.step, 10) || 1;
      const shown = parseInt(this.input.value, 10) || 0;

      pendingFocusId = button.dataset.focusId;

      if (button.dataset.action === 'remove') {
        pendingFocusId = ''; // focus falls back to the next line's remove button
        this.change(0);
        return;
      }

      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const next = button.dataset.action === 'increase' ? Math.min(shown + step, max) : Math.max(shown - step, 0);
      this.input.value = next;

      // Merge rapid taps into a single request.
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.change(parseInt(this.input.value, 10) || 0), 300);
    };

    onInputChange = () => {
      pendingFocusId = this.input.dataset.focusId;
      let value = parseInt(this.input.value, 10);
      if (Number.isNaN(value) || value < 0) value = this.currentQuantity;
      if (this.input.max) value = Math.min(value, parseInt(this.input.max, 10));
      this.input.value = value;
      this.change(value);
    };

    onInputKeydown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.input.blur();
        this.onInputChange();
      }
    };

    async change(quantity) {
      clearTimeout(this.timer);
      if (quantity === this.currentQuantity) {
        pendingFocusId = null;
        return;
      }

      this.showError('');
      this.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const response = await fetch(`${root()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: this.key, quantity, sections: bundledSections().join(',') }),
        });
        const cart = await response.json();

        if (!response.ok) {
          throw new Error(cart.description || cart.message || 'This item could not be updated.');
        }

        const title = this.dataset.title;
        const line = cart.items.find((item) => item.key === this.key);
        if (quantity === 0) {
          pendingAnnouncement = `${title} removed from your selection.`;
        } else if (line && line.quantity < quantity) {
          pendingAnnouncement = `Only ${line.quantity} of ${title} available.`;
        } else {
          pendingAnnouncement = `${title} quantity updated to ${quantity}.`;
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart } })
        );
      } catch (error) {
        const offline = error instanceof TypeError;
        pendingFocusId = null;
        this.input.value = this.currentQuantity;
        this.removeAttribute('aria-busy');
        if (offline) {
          this.showError('We couldn’t reach the store. Check your connection and try again.');
        } else {
          // Shopify can refuse a change yet still apply part of it ("Only 10 items were added…"), so re-read the
          // cart from the server and show the message on this line once the drawer has re-rendered.
          const message = error instanceof SyntaxError ? 'This item could not be updated. Please try again.' : error.message;
          pendingLineError = { key: this.key, message };
          pendingAnnouncement = message;
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message;
      this.errorElement.hidden = !message;
    }
  }

  window.customElements.define('cart-drawer-line', CartDrawerLine);

  // After Prestige swaps the drawer content, put focus back and announce what changed.
  document.addEventListener('cart-drawer:refreshed', (event) => {
    const drawer = event.target;

    if (pendingLineError) {
      const line = [...drawer.querySelectorAll('cart-drawer-line')].find((element) => element.key === pendingLineError.key);
      line?.showError(pendingLineError.message);
      pendingLineError = null;
    }

    if (pendingAnnouncement) {
      const status = drawer.querySelector('[data-cart-status]');
      if (status) {
        const message = pendingAnnouncement;
        // A fresh node; set the text a tick later so it is announced.
        setTimeout(() => (status.textContent = message), 50);
      }
      pendingAnnouncement = '';
    }

    // Only after a quantity or remove action inside the drawer: opening the drawer leaves focus to Prestige.
    if (pendingFocusId === null || !drawer.hasAttribute('open')) return;

    const target =
      (pendingFocusId && drawer.querySelector(`[data-focus-id="${CSS.escape(pendingFocusId)}"]:not([disabled])`)) ||
      drawer.querySelector('.klv-cart-line__remove, .klv-cart__button');
    pendingFocusId = null;
    target?.focus({ preventScroll: true });
  });

  // Fallback gift carousel: Shopify's related products for the first item in the cart.
  const recommendationCache = new Map();

  class CartDrawerRecommendations extends HTMLElement {
    async connectedCallback() {
      const url = this.dataset.url;
      const key = `${url}|${this.dataset.cartProducts}`;

      try {
        if (!recommendationCache.has(key)) {
          recommendationCache.set(key, fetch(url).then((response) => (response.ok ? response.text() : '')));
        }
        const html = await recommendationCache.get(key);
        const cards = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendation-cards]');

        if (!cards || !this.isConnected) return;
        this.querySelector('[data-recommendation-target]').replaceChildren(...cards.children);
        this.hidden = false;
      } catch (error) {
        // Recommendations are optional; the cart works without them.
        recommendationCache.delete(key);
      }
    }
  }

  window.customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);

  // Checkout: show a busy state so a slow redirect does not invite double taps.
  document.addEventListener('submit', (event) => {
    if (!event.target.matches('.klv-cart__footer')) return;
    const button = event.target.querySelector('.klv-cart__checkout');
    button?.setAttribute('aria-busy', 'true');
  });

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.klv-cart__checkout[aria-busy]').forEach((button) => button.removeAttribute('aria-busy'));
  });
})();
